import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatMenuModule } from '@angular/material/menu';
import { SharedModule } from '@aiwyn/shared';
import {
  CRL_BULK_ACTIONS_ENABLED,
  CrlFilterKey,
  CrlFilterOption,
  CrlSectionFilter,
  CrlSectionOption,
} from '../../../../models';

/**
 * Toolbar for the CRL list view: bulk-actions toggle, section scope, filters,
 * and search (REQ-11 / REQ-12 / PP-3642).
 *
 * Layout (left → right): [Bulk actions] · [All Sections ▾] · [Filters ▾] · [Search].
 * The bulk-actions toggle renders only while `CRL_BULK_ACTIONS_ENABLED` is set.
 *
 * Presentational only: the container owns the selection + section scope + search
 * signals and all filtering (composed through the facade over the selector's
 * display sections). This component renders:
 *  - the "Bulk actions" toggle (REQ-11 shell) — enters/exits row-selection mode,
 *  - the "All Sections" section selector (scopes the list to one section),
 *  - the seven filter buckets in a multi-select menu — each with its count,
 *  - the real-time search input.
 * Active filters are surfaced as a count badge on the trigger and a row of
 * removable chips; the user clears them individually or all at once. Every
 * change bubbles up immediately.
 */
@Component({
  selector: 'cxp-crl-search-filter-bar',
  templateUrl: './crl-search-filter-bar.component.html',
  styleUrls: ['./crl-search-filter-bar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SharedModule, MatMenuModule, MatCheckboxModule],
})
export class CrlSearchFilterBarComponent {
  readonly filterOptions = input.required<CrlFilterOption[]>();
  readonly searchQuery = input<string>('');
  /** Section scope options, including the leading "All Sections" entry (`id: null`). */
  readonly sectionOptions = input<CrlSectionOption[]>([]);
  /** Currently scoped section id, or `null` for "All Sections". */
  readonly selectedSectionId = input<CrlSectionFilter>(null);
  /** Whether bulk-actions (row-selection) mode is active. */
  readonly bulkActionsActive = input<boolean>(false);

  protected readonly bulkActionsEnabled = CRL_BULK_ACTIONS_ENABLED;

  readonly filterToggled = output<CrlFilterKey>();
  readonly searchChanged = output<string>();
  readonly clearAllFilters = output<void>();
  readonly sectionChanged = output<CrlSectionFilter>();
  readonly bulkActionsToggled = output<void>();

  /** Number of active buckets — drives the trigger badge and the "Clear all" affordance. */
  protected readonly activeCount = computed(() => this.filterOptions().filter((option) => option.selected).length);
  protected readonly hasActiveFilters = computed(() => this.activeCount() > 0);
  /** Only the selected options, for the removable chip row. */
  protected readonly activeOptions = computed(() => this.filterOptions().filter((option) => option.selected));

  /** Label for the section trigger — the selected section's label, else "All Sections". */
  protected readonly sectionTriggerLabel = computed(() => {
    const selectedId = this.selectedSectionId();
    return this.sectionOptions().find((option) => option.id === selectedId)?.label ?? 'All Sections';
  });
  /** True when a specific section is scoped (drives the trigger's active style). */
  protected readonly hasSectionScope = computed(() => this.selectedSectionId() !== null);

  onFilterToggled(key: CrlFilterKey): void {
    this.filterToggled.emit(key);
  }

  onSearchInput(value: string): void {
    this.searchChanged.emit(value);
  }

  onSectionSelected(id: CrlSectionFilter): void {
    this.sectionChanged.emit(id);
  }

  onBulkActionsToggled(): void {
    this.bulkActionsToggled.emit();
  }
}
